import { CommitmentPropsSchema } from '../../graph-schemas/pevc-ir/commitment';
import { InvestmentAccountPropsSchema } from '../../graph-schemas/pevc-ir/investment-account';
import { CapitalCallPropsSchema } from '../../graph-schemas/pevc-ir/capital-call';
import { DistributionPropsSchema } from '../../graph-schemas/pevc-ir/distribution';
import { ReportPropsSchema } from '../../graph-schemas/pevc-ir/report';
import type { NodeDefinition } from './base-crm';

export const PEVC_IR_NODES: Record<string, NodeDefinition> = {
  Commitment: {
    type: 'Commitment',
    schema: CommitmentPropsSchema,
    displayName: 'Commitment',
    displayNamePlural: 'Commitments',
    icon: 'FileSignature',
    color: '#3b82f6',
    formFields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'status', label: 'Status', type: 'select', required: true, picklistName: 'commitment_status' },
      { key: 'commitment_amount', label: 'Commitment Amount', type: 'number', required: true, format: 'currency' },
    ],
    pmnEnabled: true,
    relationshipSections: [
      {
        sectionLabel: 'Vehicle',
        icon: 'Landmark',
        edgeTypes: ['COMMITMENT_TO'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Vehicle', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'status', headerName: 'Status', width: 120, source: 'node', editable: false },
        ],
      },
      {
        sectionLabel: 'Investment Account',
        icon: 'Wallet',
        edgeTypes: ['COMMITMENT_FROM'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [{ field: 'name', headerName: 'Account', flex: 2, minWidth: 150, source: 'node', editable: false }],
      },
      {
        sectionLabel: 'Capital Calls',
        icon: 'ArrowUpCircle',
        edgeTypes: ['CALL_FOR'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Call', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'call_amount', headerName: 'Amount', width: 120, type: 'number', format: 'currency', source: 'node', editable: false },
        ],
      },
      {
        sectionLabel: 'Distributions',
        icon: 'ArrowDownCircle',
        edgeTypes: ['DISTRIBUTION_FOR'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Distribution', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'amount', headerName: 'Amount', width: 120, type: 'number', format: 'currency', source: 'node', editable: false },
        ],
      },
      {
        sectionLabel: 'Managed By',
        icon: 'UserCheck',
        edgeTypes: ['MANAGES_COMMITMENT'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Person', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'email', headerName: 'Email', flex: 1, minWidth: 150, source: 'node', editable: false },
          { field: 'role', headerName: 'Role', width: 120, source: 'edge', editable: true, editType: 'select', options: ['primary_contact', 'secondary_contact', 'authorized_signer'] },
        ],
      },
    ],
  },
  InvestmentAccount: {
    type: 'InvestmentAccount',
    schema: InvestmentAccountPropsSchema,
    displayName: 'Investment Account',
    displayNamePlural: 'Investment Accounts',
    icon: 'Wallet',
    color: '#8b5cf6',
    formFields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'type', label: 'Type', type: 'select', required: true, picklistName: 'investment_account_type' },
      { key: 'status', label: 'Status', type: 'select', required: true, picklistName: 'investment_account_status' },
    ],
    pmnEnabled: true,
    relationshipSections: [
      {
        sectionLabel: 'Organization',
        icon: 'Building2',
        edgeTypes: ['ACCOUNT_FOR'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [{ field: 'name', headerName: 'Organization', flex: 2, minWidth: 150, source: 'node', editable: false }],
      },
      {
        sectionLabel: 'Commitments',
        icon: 'FileSignature',
        edgeTypes: ['COMMITMENT_FROM'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Commitment', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'commitment_amount', headerName: 'Amount', width: 120, type: 'number', format: 'currency', source: 'node', editable: false },
        ],
      },
      {
        sectionLabel: 'Signatories',
        icon: 'UserCheck',
        edgeTypes: ['SIGNATORY_FOR'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Person', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'email', headerName: 'Email', flex: 1, minWidth: 150, source: 'node', editable: false },
          { field: 'role', headerName: 'Role', width: 120, source: 'edge', editable: true, editType: 'select', options: ['primary', 'secondary', 'authorized_signer'] },
        ],
      },
    ],
  },
  CapitalCall: {
    type: 'CapitalCall',
    schema: CapitalCallPropsSchema,
    displayName: 'Capital Call',
    displayNamePlural: 'Capital Calls',
    icon: 'ArrowUpCircle',
    color: '#ef4444',
    formFields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'status', label: 'Status', type: 'select', required: true, picklistName: 'capital_call_status' },
      { key: 'call_amount', label: 'Call Amount', type: 'number', required: true, format: 'currency' },
      { key: 'call_date', label: 'Call Date', type: 'date', required: true },
      { key: 'due_date', label: 'Due Date', type: 'date', required: true },
    ],
    pmnEnabled: false,
    relationshipSections: [
      {
        sectionLabel: 'Vehicle',
        icon: 'Landmark',
        edgeTypes: ['CALL_FROM'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [{ field: 'name', headerName: 'Vehicle', flex: 2, minWidth: 150, source: 'node', editable: false }],
      },
      {
        sectionLabel: 'Commitments',
        icon: 'FileSignature',
        edgeTypes: ['CALL_FOR'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Commitment', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'commitment_amount', headerName: 'Commitment', width: 120, type: 'number', format: 'currency', source: 'node', editable: false },
        ],
      },
    ],
  },
  Distribution: {
    type: 'Distribution',
    schema: DistributionPropsSchema,
    displayName: 'Distribution',
    displayNamePlural: 'Distributions',
    icon: 'ArrowDownCircle',
    color: '#10b981',
    formFields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'type', label: 'Type', type: 'select', required: true, picklistName: 'distribution_type' },
      { key: 'status', label: 'Status', type: 'select', required: true, picklistName: 'distribution_status' },
      { key: 'amount', label: 'Amount', type: 'number', required: true, format: 'currency' },
      { key: 'distribution_date', label: 'Distribution Date', type: 'date', required: true },
    ],
    pmnEnabled: false,
    relationshipSections: [
      {
        sectionLabel: 'Vehicle',
        icon: 'Landmark',
        edgeTypes: ['DISTRIBUTION_FROM'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [{ field: 'name', headerName: 'Vehicle', flex: 2, minWidth: 150, source: 'node', editable: false }],
      },
      {
        sectionLabel: 'Commitments',
        icon: 'FileSignature',
        edgeTypes: ['DISTRIBUTION_FOR'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [{ field: 'name', headerName: 'Commitment', flex: 2, minWidth: 150, source: 'node', editable: false }],
      },
    ],
  },
  Report: {
    type: 'Report',
    schema: ReportPropsSchema,
    displayName: 'Report',
    displayNamePlural: 'Reports',
    icon: 'FileText',
    color: '#6b7280',
    formFields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'type', label: 'Type', type: 'select', required: true, picklistName: 'report_type' },
      { key: 'status', label: 'Status', type: 'select', required: true, picklistName: 'report_status' },
      { key: 'period_end_date', label: 'Period End Date', type: 'date', required: true },
      { key: 'sent_date', label: 'Sent Date', type: 'date' },
    ],
    pmnEnabled: false,
    relationshipSections: [
      {
        sectionLabel: 'Vehicle',
        icon: 'Landmark',
        edgeTypes: ['REPORT_FOR'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [{ field: 'name', headerName: 'Vehicle', flex: 2, minWidth: 150, source: 'node', editable: false }],
      },
      {
        sectionLabel: 'Sent To',
        icon: 'FileSignature',
        edgeTypes: ['SENT_TO'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [{ field: 'name', headerName: 'Commitment', flex: 2, minWidth: 150, source: 'node', editable: false }],
      },
    ],
  },
};
